"use client";

import type { CaptionHighlightMode, CaptionStyle } from "@teksta/shared-types";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  AlignVerticalJustifyCenter,
  ArrowDownToLine,
  ArrowUpToLine,
  Check,
  ChevronDown,
  ChevronsUpDown,
  Sparkles,
} from "lucide-react";
import { useTranslations } from "next-intl";
import {
  type CSSProperties,
  memo,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { CAPTION_PRESETS } from "../_components/captions";
import { FONT_OPTIONS, loadFont, resolveFont } from "../_components/fonts";
import { ColorField } from "./ColorField";
import { parseColor, rgbaString } from "./color";

const FONTS = FONT_OPTIONS.map((f) => ({ label: f.label, value: f.value }));
const DEFAULT_BG = { color: "rgba(0,0,0,0.66)", paddingRel: 0.35, radiusRel: 0.25 };
const DEFAULT_SHADOW = { color: "rgba(0,0,0,0.72)", offsetYRel: 0.12, blurRel: 0.08 };
const DEFAULT_FADE = { inSec: 0.25, outSec: 0.25 };
const DEFAULT_HIGHLIGHT_BOX = { color: "#facc15", paddingRel: 0.2, radiusRel: 0.18 };

function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="space-y-2.5 border-t pt-3 first:border-t-0 first:pt-0">
      {title && <p className="font-medium text-sm">{title}</p>}
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-foreground/70 text-sm">{label}</span>
      {children}
    </div>
  );
}

/**
 * A toggle row where the whole row is the hit target — the {@link Switch} is
 * nested in a `<label>`, so clicking the label text (or anywhere in the row)
 * flips it, not just the small switch. Implicit label association keeps it
 * accessible without an explicit id.
 *
 * The label itself can *demonstrate* the effect it toggles (via `labelClassName`
 * / `labelStyle`) — e.g. "Pop" literally pops, "Drop shadow" casts one — so the
 * option is self-explanatory without a separate preview.
 */
function SwitchField({
  label,
  checked,
  onCheckedChange,
  disabled,
  labelClassName,
  labelStyle,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  labelClassName?: string;
  labelStyle?: CSSProperties;
}) {
  return (
    <label
      className={cn(
        "-mx-1 flex items-center justify-between gap-2 rounded px-1 py-1",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-foreground/5"
      )}
    >
      <span
        className={cn("select-none text-foreground/70 text-sm", labelClassName)}
        style={labelStyle}
      >
        {label}
      </span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </label>
  );
}

/**
 * One font row, rendered in its own typeface so the menu doubles as a specimen.
 * The webfont is fetched lazily: an {@link IntersectionObserver} (scoped to the
 * scrolling list via `listRef`) only calls {@link loadFont} once the row is about
 * to be visible — so opening the picker doesn't fetch all ~260 families, just the
 * dozen on screen. Once loaded, the `<span>`'s `font-family` swaps in on its own
 * (no re-render needed). Pre-warming the visible rows also means the font the user
 * finally clicks is already loaded, so the live preview updates instantly.
 */
function FontPreviewItem({
  option,
  selected,
  onSelect,
  listRef,
}: {
  option: { label: string; value: string };
  selected: boolean;
  onSelect: () => void;
  listRef: RefObject<HTMLDivElement | null>;
}) {
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;
    // Child refs (this span) and parent refs (the list) are both assigned before
    // any effect runs, so `listRef.current` is the live scroll root here.
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadFont(option.value);
          io.disconnect();
        }
      },
      { root: listRef.current, rootMargin: "150px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [option.value, listRef]);

  return (
    <CommandItem value={option.label} onSelect={onSelect}>
      <Check className={cn("mr-2 h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
      <span
        ref={spanRef}
        className="truncate text-base leading-tight"
        style={{ fontFamily: option.value }}
      >
        {option.label}
      </span>
    </CommandItem>
  );
}

function FontCombobox({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const t = useTranslations("mediaTools.editor");
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-8 w-52 justify-between px-2 font-normal"
        >
          <span className="truncate" style={selected ? { fontFamily: selected.value } : undefined}>
            {selected?.label ?? t("style.selectFont")}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[18rem] p-0" align="end">
        <Command>
          <CommandInput placeholder={t("style.searchFonts")} />
          <CommandList ref={listRef} className="max-h-72">
            <CommandEmpty>{t("style.noFontsFound")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <FontPreviewItem
                  key={option.value}
                  option={option}
                  selected={option.value === value}
                  listRef={listRef}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Selector({
  value,
  options,
  onChange,
  optionStyle,
  disabled,
}: {
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
  /**
   * Per-option inline style (e.g. render each font/weight in itself). Browsers
   * honor `font-family`/`font-weight` on `<option>`; Safari ignores per-option
   * fonts in native selects, so treat it as a graceful enhancement.
   */
  optionStyle?: (value: string) => CSSProperties;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      style={optionStyle?.(value)}
      className="rounded border bg-background px-2 py-1 text-sm outline-hidden disabled:opacity-50"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value} style={optionStyle?.(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
  onCommit,
  disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
  onCommit: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-foreground/70">{label}</span>
        <span className="text-foreground/40 tabular-nums">{format(value)}</span>
      </div>
      <input
        type="range"
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        onPointerUp={onCommit}
        onBlur={onCommit}
        className="w-full cursor-pointer accent-primary disabled:opacity-50"
      />
    </div>
  );
}

const pct = (value: number) => `${Math.round(value * 100)}%`;
const seconds = (value: number) => `${value.toFixed(2)}s`;

/**
 * A CSS approximation of a {@link CaptionStyle} for the preset specimen chips, at
 * a fixed sample size so styles are comparable regardless of their relative font
 * size. Not pixel-identical to the canvas burn (it uses `-webkit-text-stroke`
 * rather than the burn's behind-fill stroke), but faithful enough to tell the
 * presets apart at a glance.
 */
function presetPreviewStyle(style: CaptionStyle): CSSProperties {
  const size = 20;
  const css: CSSProperties & Record<string, string | number> = {
    fontFamily: style.font,
    fontWeight: style.fontWeight,
    color: style.color,
    fontSize: size,
    lineHeight: 1,
  };
  if (style.stroke.widthRel > 0) {
    css.WebkitTextStrokeWidth = `${Math.max(0.5, style.stroke.widthRel * size).toFixed(1)}px`;
    css.WebkitTextStrokeColor = style.stroke.color;
    css.paintOrder = "stroke fill";
  }
  if (style.shadow) {
    css.textShadow = `0 ${(style.shadow.offsetYRel * size).toFixed(1)}px ${(
      style.shadow.blurRel * size
    ).toFixed(1)}px ${style.shadow.color}`;
  }
  if (style.background) {
    css.backgroundColor = style.background.color;
    css.padding = `${(style.background.paddingRel * size * 0.4).toFixed(1)}px ${(
      style.background.paddingRel * size
    ).toFixed(1)}px`;
    css.borderRadius = `${(style.background.radiusRel * size).toFixed(1)}px`;
  }
  return css;
}

/** A toolbar button that opens a popover of related style controls. */
function StyleMenu({
  label,
  disabled,
  contentClassName,
  children,
}: {
  label: string;
  disabled?: boolean;
  contentClassName?: string;
  children: ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-8 gap-1 px-2.5 font-normal"
        >
          {label}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("max-h-[70vh] w-72 space-y-2.5 overflow-y-auto", contentClassName)}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

export type StyleGroupId = "presets" | "text" | "box" | "effects" | "highlight";

interface StyleControlProps {
  style: CaptionStyle;
  onStyle: (patch: Partial<CaptionStyle>, key?: string | null) => void;
  endGroup: () => void;
  disabled?: boolean;
}

/** Presets — each entry is a specimen rendered in its own caption style. */
function PresetGrid({
  disabled,
  onApplyPreset,
  onApplied,
}: {
  disabled?: boolean;
  onApplyPreset: (id: string) => void;
  onApplied?: () => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {CAPTION_PRESETS.map((preset) => (
        <button
          key={preset.id}
          type="button"
          disabled={disabled}
          title={preset.label}
          onClick={() => {
            onApplyPreset(preset.id);
            onApplied?.();
          }}
          className="flex h-14 items-center justify-center overflow-hidden rounded-md border bg-zinc-800 px-2 hover:border-primary disabled:opacity-50"
        >
          <span className="max-w-full truncate" style={presetPreviewStyle(preset.style)}>
            {preset.label}
          </span>
        </button>
      ))}
    </div>
  );
}

/** Desktop popover wrapper for caption presets. */
function PresetMenu({
  disabled,
  onApplyPreset,
}: {
  disabled?: boolean;
  onApplyPreset: (id: string) => void;
}) {
  const t = useTranslations("mediaTools.editor");
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-8 gap-1 px-2.5 font-normal"
        >
          <Sparkles className="h-3.5 w-3.5 opacity-70" />
          {t("style.groups.presets")}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-88">
        <PresetGrid
          disabled={disabled}
          onApplyPreset={onApplyPreset}
          onApplied={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

function TextStyleControls({ style, onStyle, endGroup, disabled }: StyleControlProps) {
  const t = useTranslations("mediaTools.editor");
  const weightOptions = useMemo(() => {
    const weights = resolveFont(style.font)?.weights ?? [style.fontWeight];
    return Array.from(new Set([...weights, style.fontWeight]))
      .sort((a, b) => a - b)
      .map((weight) => ({
        label: t(`style.weights.${weight}`) ?? String(weight),
        value: String(weight),
      }));
  }, [style.font, style.fontWeight, t]);

  return (
    <>
      <Field label={t("style.fields.font")}>
        <FontCombobox
          value={style.font}
          options={FONTS}
          disabled={disabled}
          onChange={(value) => onStyle({ font: value })}
        />
      </Field>
      <Field label={t("style.fields.weight")}>
        <Selector
          value={String(style.fontWeight)}
          options={weightOptions}
          disabled={disabled}
          optionStyle={(value) => ({ fontWeight: Number(value) })}
          onChange={(value) => onStyle({ fontWeight: Number(value) })}
        />
      </Field>
      <Slider
        label={t("style.fields.size")}
        value={style.fontSizeRel}
        min={0.03}
        max={0.12}
        step={0.002}
        format={pct}
        disabled={disabled}
        onChange={(value) => onStyle({ fontSizeRel: value }, "size")}
        onCommit={endGroup}
      />
      <ColorField
        label={t("style.fields.color")}
        value={parseColor(style.color).hex}
        disabled={disabled}
        onChange={(hex) => onStyle({ color: hex }, "text-color")}
        onCommit={endGroup}
      />
      <Field label={t("style.fields.align")}>
        <ToggleGroup
          type="single"
          size="sm"
          value={style.align}
          onValueChange={(value) => {
            if (value) onStyle({ align: value as CaptionStyle["align"] });
          }}
        >
          <ToggleGroupItem value="left" aria-label={t("style.align.left")} disabled={disabled}>
            <AlignLeft className="h-4 w-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="center" aria-label={t("style.align.center")} disabled={disabled}>
            <AlignCenter className="h-4 w-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="right" aria-label={t("style.align.right")} disabled={disabled}>
            <AlignRight className="h-4 w-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </Field>
      <Field label={t("style.fields.position")}>
        <ToggleGroup
          type="single"
          size="sm"
          value={style.position}
          onValueChange={(value) => {
            if (value) onStyle({ position: value as CaptionStyle["position"] });
          }}
        >
          <ToggleGroupItem value="top" aria-label={t("style.positions.top")} disabled={disabled}>
            <ArrowUpToLine className="h-4 w-4" />
          </ToggleGroupItem>
          <ToggleGroupItem
            value="center"
            aria-label={t("style.positions.middle")}
            disabled={disabled}
          >
            <AlignVerticalJustifyCenter className="h-4 w-4" />
          </ToggleGroupItem>
          <ToggleGroupItem
            value="bottom"
            aria-label={t("style.positions.bottom")}
            disabled={disabled}
          >
            <ArrowDownToLine className="h-4 w-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </Field>
      <Slider
        label={t("style.fields.offset")}
        value={style.offsetRel}
        min={0}
        max={0.3}
        step={0.005}
        format={pct}
        disabled={disabled}
        onChange={(value) => onStyle({ offsetRel: value }, "offset")}
        onCommit={endGroup}
      />
    </>
  );
}

function BoxStyleControls({ style, onStyle, endGroup, disabled }: StyleControlProps) {
  const t = useTranslations("mediaTools.editor");
  const bg = style.background;
  const bgColor = bg ? parseColor(bg.color) : null;

  return (
    <>
      <Section title={t("style.sections.outline")}>
        <Slider
          label={t("style.fields.width")}
          value={style.stroke.widthRel}
          min={0}
          max={0.25}
          step={0.005}
          format={pct}
          disabled={disabled}
          onChange={(value) =>
            onStyle({ stroke: { ...style.stroke, widthRel: value } }, "stroke-w")
          }
          onCommit={endGroup}
        />
        <ColorField
          label={t("style.fields.color")}
          value={parseColor(style.stroke.color).hex}
          disabled={disabled}
          onChange={(hex) => onStyle({ stroke: { ...style.stroke, color: hex } }, "stroke-c")}
          onCommit={endGroup}
        />
      </Section>

      <Section title={t("style.sections.background")}>
        <SwitchField
          label={t("style.fields.box")}
          checked={bg !== null}
          disabled={disabled}
          labelStyle={{
            background: "rgba(0,0,0,0.72)",
            color: "#fff",
            borderRadius: "0.25rem",
            padding: "0.05rem 0.4rem",
          }}
          onCheckedChange={(on) => onStyle({ background: on ? (bg ?? DEFAULT_BG) : null })}
        />
        {bg && bgColor && (
          <>
            <ColorField
              label={t("style.fields.color")}
              value={bgColor.hex}
              disabled={disabled}
              onChange={(hex) =>
                onStyle({ background: { ...bg, color: rgbaString(hex, bgColor.alpha) } }, "bg-c")
              }
              onCommit={endGroup}
            />
            <Slider
              label={t("style.fields.opacity")}
              value={bgColor.alpha}
              min={0}
              max={1}
              step={0.05}
              format={pct}
              disabled={disabled}
              onChange={(value) =>
                onStyle({ background: { ...bg, color: rgbaString(bgColor.hex, value) } }, "bg-a")
              }
              onCommit={endGroup}
            />
            <Slider
              label={t("style.fields.padding")}
              value={bg.paddingRel}
              min={0}
              max={0.8}
              step={0.02}
              format={pct}
              disabled={disabled}
              onChange={(value) => onStyle({ background: { ...bg, paddingRel: value } }, "bg-p")}
              onCommit={endGroup}
            />
            <Slider
              label={t("style.fields.cornerRadius")}
              value={bg.radiusRel}
              min={0}
              max={0.8}
              step={0.02}
              format={pct}
              disabled={disabled}
              onChange={(value) => onStyle({ background: { ...bg, radiusRel: value } }, "bg-r")}
              onCommit={endGroup}
            />
          </>
        )}
      </Section>
    </>
  );
}

function EffectsStyleControls({ style, onStyle, endGroup, disabled }: StyleControlProps) {
  const t = useTranslations("mediaTools.editor");
  const shadow = style.shadow ?? null;
  const shadowColor = shadow ? parseColor(shadow.color) : null;
  const fade = style.fade ?? null;

  return (
    <>
      <Section title={t("style.sections.shadow")}>
        <SwitchField
          label={t("style.fields.dropShadow")}
          checked={shadow !== null}
          disabled={disabled}
          labelClassName="font-medium text-foreground"
          labelStyle={{ textShadow: "1.5px 2px 0 rgba(0,0,0,0.45)" }}
          onCheckedChange={(on) => onStyle({ shadow: on ? (shadow ?? DEFAULT_SHADOW) : null })}
        />
        {shadow && shadowColor && (
          <>
            <ColorField
              label={t("style.fields.color")}
              value={shadowColor.hex}
              disabled={disabled}
              onChange={(hex) =>
                onStyle(
                  { shadow: { ...shadow, color: rgbaString(hex, shadowColor.alpha) } },
                  "shadow-c"
                )
              }
              onCommit={endGroup}
            />
            <Slider
              label={t("style.fields.opacity")}
              value={shadowColor.alpha}
              min={0}
              max={1}
              step={0.05}
              format={pct}
              disabled={disabled}
              onChange={(value) =>
                onStyle(
                  { shadow: { ...shadow, color: rgbaString(shadowColor.hex, value) } },
                  "shadow-a"
                )
              }
              onCommit={endGroup}
            />
            <Slider
              label={t("style.fields.verticalOffset")}
              value={shadow.offsetYRel}
              min={0}
              max={0.5}
              step={0.01}
              format={pct}
              disabled={disabled}
              onChange={(value) =>
                onStyle({ shadow: { ...shadow, offsetYRel: value } }, "shadow-y")
              }
              onCommit={endGroup}
            />
            <Slider
              label={t("style.fields.blur")}
              value={shadow.blurRel}
              min={0}
              max={0.4}
              step={0.01}
              format={pct}
              disabled={disabled}
              onChange={(value) =>
                onStyle({ shadow: { ...shadow, blurRel: value } }, "shadow-blur")
              }
              onCommit={endGroup}
            />
          </>
        )}
      </Section>

      <Section title={t("style.sections.fade")}>
        <SwitchField
          label={t("style.fields.cueEdges")}
          checked={fade !== null}
          disabled={disabled}
          labelClassName="animate-caption-fade font-medium text-foreground"
          onCheckedChange={(on) => onStyle({ fade: on ? (fade ?? DEFAULT_FADE) : null })}
        />
        {fade && (
          <>
            <Slider
              label={t("style.fields.fadeIn")}
              value={fade.inSec}
              min={0}
              max={1}
              step={0.05}
              format={seconds}
              disabled={disabled}
              onChange={(value) => onStyle({ fade: { ...fade, inSec: value } }, "fade-in")}
              onCommit={endGroup}
            />
            <Slider
              label={t("style.fields.fadeOut")}
              value={fade.outSec}
              min={0}
              max={1}
              step={0.05}
              format={seconds}
              disabled={disabled}
              onChange={(value) => onStyle({ fade: { ...fade, outSec: value } }, "fade-out")}
              onCommit={endGroup}
            />
          </>
        )}
      </Section>
    </>
  );
}

function HighlightStyleControls({ style, onStyle, endGroup, disabled }: StyleControlProps) {
  const t = useTranslations("mediaTools.editor");
  const highlightBox = style.highlight.box ?? null;
  const highlightBoxColor = highlightBox ? parseColor(highlightBox.color) : null;
  // "Pop" emphasises the active word, which only reads once the word is already
  // distinguished — by a colour mode or the box. Reveal it only then (and clear
  // it when all highlight is removed, so it's never on while hidden).
  const hasActiveWordHighlight = style.highlight.mode !== "none" || highlightBox !== null;

  const highlightOptions: { value: CaptionHighlightMode; label: string }[] = [
    { value: "none", label: t("style.highlightModes.none") },
    { value: "basic", label: t("style.highlightModes.basic") },
    { value: "creeping", label: t("style.highlightModes.creeping") },
    { value: "sliding", label: t("style.highlightModes.sliding") },
  ];

  return (
    <>
      <Field label={t("style.fields.mode")}>
        <Selector
          value={style.highlight.mode}
          options={highlightOptions}
          disabled={disabled}
          onChange={(value) => {
            const mode = value as CaptionHighlightMode;
            // Removing the last highlight (mode → none, no box) also drops pop,
            // so it can't linger active while its toggle is hidden.
            const pop = mode === "none" && highlightBox === null ? false : style.highlight.pop;
            onStyle({ highlight: { ...style.highlight, mode, pop } });
          }}
        />
      </Field>
      {style.highlight.mode !== "none" && (
        <ColorField
          label={t("style.fields.color")}
          value={parseColor(style.highlight.color).hex}
          disabled={disabled}
          onChange={(hex) => onStyle({ highlight: { ...style.highlight, color: hex } }, "hl-c")}
          onCommit={endGroup}
        />
      )}
      <SwitchField
        label={t("style.fields.activeBox")}
        checked={highlightBox !== null}
        disabled={disabled}
        labelStyle={{
          background: DEFAULT_HIGHLIGHT_BOX.color,
          color: "#000",
          borderRadius: "0.25rem",
          padding: "0.05rem 0.4rem",
        }}
        onCheckedChange={(on) =>
          onStyle({
            highlight: {
              ...style.highlight,
              box: on ? (highlightBox ?? DEFAULT_HIGHLIGHT_BOX) : null,
              // Turning the box off with no colour mode leaves nothing to pop.
              pop: !on && style.highlight.mode === "none" ? false : style.highlight.pop,
            },
          })
        }
      />
      {highlightBox && highlightBoxColor && (
        <>
          <ColorField
            label={t("style.fields.boxColor")}
            value={highlightBoxColor.hex}
            disabled={disabled}
            onChange={(hex) =>
              onStyle(
                {
                  highlight: {
                    ...style.highlight,
                    box: {
                      ...highlightBox,
                      color: rgbaString(hex, highlightBoxColor.alpha),
                    },
                  },
                },
                "hl-box-c"
              )
            }
            onCommit={endGroup}
          />
          <Slider
            label={t("style.fields.boxOpacity")}
            value={highlightBoxColor.alpha}
            min={0}
            max={1}
            step={0.05}
            format={pct}
            disabled={disabled}
            onChange={(value) =>
              onStyle(
                {
                  highlight: {
                    ...style.highlight,
                    box: {
                      ...highlightBox,
                      color: rgbaString(highlightBoxColor.hex, value),
                    },
                  },
                },
                "hl-box-a"
              )
            }
            onCommit={endGroup}
          />
          <Slider
            label={t("style.fields.boxPadding")}
            value={highlightBox.paddingRel}
            min={0}
            max={0.8}
            step={0.02}
            format={pct}
            disabled={disabled}
            onChange={(value) =>
              onStyle(
                {
                  highlight: {
                    ...style.highlight,
                    box: { ...highlightBox, paddingRel: value },
                  },
                },
                "hl-box-p"
              )
            }
            onCommit={endGroup}
          />
          <Slider
            label={t("style.fields.boxRadius")}
            value={highlightBox.radiusRel}
            min={0}
            max={0.8}
            step={0.02}
            format={pct}
            disabled={disabled}
            onChange={(value) =>
              onStyle(
                {
                  highlight: {
                    ...style.highlight,
                    box: { ...highlightBox, radiusRel: value },
                  },
                },
                "hl-box-r"
              )
            }
            onCommit={endGroup}
          />
        </>
      )}
      {hasActiveWordHighlight && (
        <SwitchField
          label={t("style.fields.pop")}
          checked={Boolean(style.highlight.pop)}
          disabled={disabled}
          labelClassName="inline-block origin-left animate-caption-pop font-semibold text-foreground"
          onCheckedChange={(on) => onStyle({ highlight: { ...style.highlight, pop: on } })}
        />
      )}
    </>
  );
}

export function StyleGroupContent({
  group,
  onApplyPreset,
  onPresetApplied,
  ...controlProps
}: StyleControlProps & {
  group: StyleGroupId;
  onApplyPreset: (id: string) => void;
  onPresetApplied?: () => void;
}) {
  if (group === "presets") {
    return (
      <PresetGrid
        disabled={controlProps.disabled}
        onApplyPreset={onApplyPreset}
        onApplied={onPresetApplied}
      />
    );
  }
  if (group === "text") return <TextStyleControls {...controlProps} />;
  if (group === "box") return <BoxStyleControls {...controlProps} />;
  if (group === "effects") return <EffectsStyleControls {...controlProps} />;
  return <HighlightStyleControls {...controlProps} />;
}

/**
 * Caption-style toolbar. Sits above the video as a row of popover menus so the
 * controls don't eat vertical space or push the preview off-screen. Presets are
 * *apply* actions (not a selected state), so the controls always reflect the real
 * `doc.style` — undo can't desync them. Every {@link CaptionStyle} field has a
 * control; nested groups (stroke/background/highlight) are patched whole.
 * Continuous controls (sliders, color pickers) pass an undo key and call
 * `endGroup` on release so a gesture is one undo step. Memoized — it doesn't
 * depend on the playhead, so it doesn't re-render during playback.
 */
export const StyleInspector = memo(function StyleInspector({
  style,
  onStyle,
  endGroup,
  onApplyPreset,
  disabled = false,
  scopeHint,
}: {
  style: CaptionStyle;
  onStyle: (patch: Partial<CaptionStyle>, key?: string | null) => void;
  endGroup: () => void;
  onApplyPreset: (id: string) => void;
  disabled?: boolean;
  /** Shown when the edits target something narrower than the whole layer (e.g. one label track). */
  scopeHint?: string;
}) {
  const t = useTranslations("mediaTools.editor");
  const controlProps = { style, onStyle, endGroup, disabled };

  return (
    <div className="flex flex-wrap items-center gap-1.5 py-1.5">
      <PresetMenu disabled={disabled} onApplyPreset={onApplyPreset} />
      <span className="mx-0.5 h-5 w-px shrink-0 bg-border" aria-hidden="true" />

      <StyleMenu label={t("style.groups.text")} disabled={disabled}>
        <TextStyleControls {...controlProps} />
      </StyleMenu>

      <StyleMenu label={t("style.groups.box")} disabled={disabled}>
        <BoxStyleControls {...controlProps} />
      </StyleMenu>

      <StyleMenu label={t("style.groups.effects")} disabled={disabled}>
        <EffectsStyleControls {...controlProps} />
      </StyleMenu>

      <StyleMenu label={t("style.groups.highlight")} disabled={disabled} contentClassName="w-80">
        <HighlightStyleControls {...controlProps} />
      </StyleMenu>

      {scopeHint !== undefined && (
        <span className="ml-auto shrink-0 rounded bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
          {scopeHint}
        </span>
      )}
    </div>
  );
});
